import { useHtClient } from "../providers/ht";
import { useAuthSession } from "../lib/auth";
import { useSupabaseClient } from "../providers/supabase";
import { useLocation, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { getSupabaseClient } from "../providers/supabase";

import { View } from "react-native-web";
import CustomText from "../components/atoms/CustomText";

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { data: session, isLoading: isSessionLoading, isError: isSessionError } = useAuthSession();
  const { data: supabase, isLoading: isSupabaseLoading } = useSupabaseClient();
  const { data: client, isLoading: isClientLoading } = useHtClient();
  const location = useLocation();

  // Advisor portal pages need a two-factor session (aal2). Checked from the local
  // session token; the backend enforces the same rule on every staff request.
  const needsTwoFactor = location.pathname.startsWith("/advisor");
  const [aal, setAal] = useState<string | null>(needsTwoFactor ? null : "n/a");
  useEffect(() => {
    if (!needsTwoFactor) return;
    getSupabaseClient()
      .auth.mfa.getAuthenticatorAssuranceLevel()
      .then(({ data }) => setAal(data?.currentLevel || "aal1"));
  }, [needsTwoFactor, session?.access_token]);

  console.log('🔒 PROTECTED ROUTE: State', {
    hasSession: !!session,
    sessionLoading: isSessionLoading,
    sessionError: isSessionError,
    hasSupabase: !!supabase,
    supabaseLoading: isSupabaseLoading,
    hasClient: !!client,
    clientLoading: isClientLoading,
    path: location.pathname
  });

  // Wait for all clients to be ready
  if (isSupabaseLoading || isSessionLoading || isClientLoading) {
    console.log('⏳ PROTECTED ROUTE: Loading dependencies...', {
      supabaseLoading: isSupabaseLoading,
      sessionLoading: isSessionLoading,
      clientLoading: isClientLoading
    });
    return (
      <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
        <CustomText>Loading...</CustomText>
      </View>
    );
  }

  // Only redirect if we're sure there's no session and all clients are ready
  if (!isSupabaseLoading && !isSessionLoading && !isClientLoading && (!session || isSessionError || !client)) {
    console.log('🚫 PROTECTED ROUTE: Access denied', {
      hasSession: !!session,
      sessionError: isSessionError,
      hasClient: !!client,
      redirectingTo: '/login'
    });
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (needsTwoFactor && aal === null) {
    return (
      <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
        <CustomText>Loading...</CustomText>
      </View>
    );
  }
  if (needsTwoFactor && aal !== "aal2") {
    return <Navigate to="/mfa" state={{ next: location.pathname }} replace />;
  }

  console.log('✅ PROTECTED ROUTE: Access granted', {
    userId: session?.user?.id,
    path: location.pathname
  });

  return <>{children}</>;
};

export default ProtectedRoute;
